/**
 * WhatsApp utility functions for invoice sharing
 */

/**
 * Format phone number for WhatsApp
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number with country code
 */
export const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return ''
  
  // Remove all non-digit characters
  let cleanPhone = phone.replace(/\D/g, '')
  
  // If phone starts with +91, remove it temporarily
  if (cleanPhone.startsWith('91')) {
    cleanPhone = cleanPhone.substring(2)
  }
  
  // If phone starts with 0, remove it
  if (cleanPhone.startsWith('0')) {
    cleanPhone = cleanPhone.substring(1)
  }
  
  // Validate Indian mobile number (10 digits)
  if (cleanPhone.length === 10 && /^[6-9]\d{9}$/.test(cleanPhone)) {
    return `91${cleanPhone}` // Add India country code
  }
  
  // If already has country code and is valid
  if (cleanPhone.length === 12 && cleanPhone.startsWith('91')) {
    return cleanPhone
  }
  
  return '' // Invalid phone number
}

/**
 * Generate WhatsApp message for invoice
 * @param {Object} transaction - Transaction object
 * @param {Object} business - Business object
 * @returns {string} Formatted WhatsApp message
 */
export const generateWhatsAppMessage = (transaction, business) => {
  const customerName = transaction.customer_name && transaction.customer_name !== 'Walk-in Customer' 
    ? transaction.customer_name 
    : 'Valued Customer'
  
  const businessName = business?.name || 'Our Store'
  const invoiceNumber = transaction.invoice_number || transaction._id
  const date = new Date(transaction.created_at || Date.now()).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
  const total = `₹${parseFloat(transaction.total || 0).toFixed(2)}`
  const paymentMethod = transaction.payment_method || 'Cash'
  
  // Format business address
  let businessAddress = ''
  if (business?.address) {
    const address = business.address
    const addressParts = []
    if (address.street) addressParts.push(address.street)
    if (address.city) addressParts.push(address.city)
    if (address.state) addressParts.push(address.state)
    if (address.pincode) addressParts.push(address.pincode)
    
    if (addressParts.length > 0) {
      businessAddress = addressParts.join(', ')
    }
  }
  
  // Format business contact
  let businessContact = ''
  if (business?.contact?.phone) {
    businessContact = business.contact.phone
  }
  
  // Format items list
  let itemsList = ''
  if (transaction.items && transaction.items.length > 0) {
    itemsList = transaction.items.map(item => {
      const quantity = parseFloat(item.quantity || 1)
      const price = parseFloat(item.unit_price || 0)
      const itemTotal = quantity * price
      return `• ${item.product_name || item.name || 'Item'} (${quantity} × ₹${price.toFixed(2)}) — ₹${itemTotal.toFixed(2)}`
    }).join('\n')
  }
  
  // Discount info if applicable
  let discountInfo = ''
  if (transaction.discount && transaction.discount > 0) {
    const discountType = transaction.discount_type === 'percentage' ? '%' : '₹'
    discountInfo = `\n\n🎯 Discount: ${transaction.discount}${discountType}`
  }
  
  // GST summary if available
  let gstInfo = ''
  if (transaction.gst_summary && transaction.gst_summary.total_gst > 0) {
    gstInfo = `\n\n💰 Tax Details:\nTotal GST: ₹${parseFloat(transaction.gst_summary.total_gst).toFixed(2)}`
    
    if (transaction.gst_summary.cgst > 0) {
      gstInfo += `\nCGST: ₹${parseFloat(transaction.gst_summary.cgst).toFixed(2)}`
      gstInfo += `\nSGST: ₹${parseFloat(transaction.gst_summary.sgst).toFixed(2)}`
    }
    if (transaction.gst_summary.igst > 0) {
      gstInfo += `\nIGST: ₹${parseFloat(transaction.gst_summary.igst).toFixed(2)}`
    }
  }
  
  // Build business info section
  let businessInfo = `🏪 ${businessName}`
  if (businessAddress) {
    businessInfo += `\n📍 ${businessAddress}`
  }
  if (businessContact) {
    businessInfo += `\n📞 ${businessContact}`
  }
  
  // Add customer greeting if not walk-in customer
  const customerGreeting = customerName !== 'Valued Customer' ? `\n\nHi ${customerName}! 👋` : ''
  
  const message = `${businessInfo}${customerGreeting}

━━━━━━━━━━━━━━━
🧾 Invoice: ${invoiceNumber}
📅 Date: ${date}
💳 Payment Mode: ${paymentMethod}
━━━━━━━━━━━━━━━

🛍️ Items Purchased
${itemsList}${discountInfo}${gstInfo}
━━━━━━━━━━━━━━━
💰 Total Amount: ${total}
━━━━━━━━━━━━━━━

🙏 Thank you for choosing ${businessName}!
Your support means a lot to us.
Have a wonderful day! 😊`

  return message
}

/**
 * Generate WhatsApp URL
 * @param {string} phone - Formatted phone number
 * @param {string} message - Message to send
 * @returns {string} WhatsApp URL
 */
export const generateWhatsAppURL = (phone, message) => {
  if (!phone || !message) return ''
  
  const encodedMessage = encodeURIComponent(message)
  return `https://wa.me/${phone}?text=${encodedMessage}`
}

/**
 * Validate if customer is eligible for WhatsApp sharing
 * @param {Object} transaction - Transaction object
 * @returns {boolean} True if eligible for WhatsApp sharing
 */
export const isEligibleForWhatsAppShare = (transaction) => {
  // Must not be walk-in customer
  if (!transaction.customer_name || transaction.customer_name === 'Walk-in Customer') {
    return false
  }
  
  // Must have mobile number
  if (!transaction.customer_mobile) {
    return false
  }
  
  // Validate phone number format
  const formattedPhone = formatPhoneForWhatsApp(transaction.customer_mobile)
  return formattedPhone !== ''
}

/**
 * Open WhatsApp with message
 * @param {string} phone - Phone number
 * @param {string} message - Message to send
 */
export const openWhatsApp = (phone, message) => {
  const url = generateWhatsAppURL(phone, message)
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}